import { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

type Mode = 'compound' | 'loan';

/** 複利：本利和 = 本金 * (1 + r)^n */
function compound(
  principal: number,
  ratePercent: number,
  years: number
): number {
  const r = ratePercent / 100;
  return Math.round(principal * Math.pow(1 + r, years));
}

/** 房貸本息攤還：每月應付 = P * [r(1+r)^n] / [(1+r)^n - 1] */
function monthlyPayment(
  principal: number,
  annualRatePercent: number,
  years: number
): number {
  const r = annualRatePercent / 100 / 12;
  const n = years * 12;
  if (r === 0) return Math.round(principal / n);
  return Math.round(
    (principal * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1)
  );
}

/** 複利模式：每年本利和（第 0 年＝本金） */
function getCompoundYearlyData(
  principal: number,
  ratePercent: number,
  years: number
): Array<{ year: number; 本利和: number }> {
  const data: Array<{ year: number; 本利和: number }> = [];
  for (let y = 0; y <= years; y++) {
    data.push({ year: y, 本利和: compound(principal, ratePercent, y) });
  }
  return data;
}

/** 房貸模式：每年底剩餘本金與累計已還利息 */
function getLoanYearlyData(
  principal: number,
  annualRatePercent: number,
  years: number,
  monthly: number
): Array<{ year: number; 剩餘本金: number; 累計已還利息: number }> {
  const monthlyRate = annualRatePercent / 100 / 12;
  let balance = principal;
  let totalInterestPaid = 0;
  const data: Array<{
    year: number;
    剩餘本金: number;
    累計已還利息: number;
  }> = [{ year: 0, 剩餘本金: principal, 累計已還利息: 0 }];

  for (let y = 1; y <= years; y++) {
    for (let m = 0; m < 12 && balance > 0; m++) {
      const interest = Math.round(balance * monthlyRate);
      const principalPaid = Math.min(monthly - interest, balance);
      totalInterestPaid += interest;
      balance = Math.max(0, Math.round(balance - principalPaid));
    }
    data.push({
      year: y,
      剩餘本金: balance,
      累計已還利息: totalInterestPaid,
    });
  }
  return data;
}

/**
 * 複利・房貸計算器
 */
export function CompoundLoanCalculatorTool() {
  const [mode, setMode] = useState<Mode>('compound');
  const [principal, setPrincipal] = useState('1000000');
  const [rate, setRate] = useState('2');
  const [years, setYears] = useState('10');

  const P = Math.floor(Number(principal)) || 0;
  const R = Number(rate) || 0;
  const Y = Number(years) || 0;

  const compoundResult = P > 0 && R >= 0 && Y > 0 ? compound(P, R, Y) : null;
  const monthly = P > 0 && R >= 0 && Y > 0 ? monthlyPayment(P, R, Y) : null;
  const totalPayment = monthly != null ? monthly * Y * 12 : null;
  const totalInterest = totalPayment != null && P > 0 ? totalPayment - P : null;

  const compoundYearlyData =
    P > 0 && R >= 0 && Y > 0 ? getCompoundYearlyData(P, R, Y) : [];
  const loanYearlyData =
    P > 0 && R >= 0 && Y > 0 && monthly != null
      ? getLoanYearlyData(P, R, Y, monthly)
      : [];

  return (
    <>
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-4 flex gap-2">
          <button
            type="button"
            onClick={() => setMode('compound')}
            className={`rounded px-4 py-2 text-sm font-medium ${
              mode === 'compound'
                ? 'bg-primary text-white'
                : 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
            }`}
          >
            複利試算
          </button>
          <button
            type="button"
            onClick={() => setMode('loan')}
            className={`rounded px-4 py-2 text-sm font-medium ${
              mode === 'loan'
                ? 'bg-primary text-white'
                : 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
            }`}
          >
            房貸試算
          </button>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">
              {mode === 'loan' ? '貸款金額（元）' : '本金（元）'}
            </label>
            <input
              type="number"
              min="0"
              value={principal}
              onChange={e => setPrincipal(e.target.value)}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">
              年利率（%）
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={rate}
              onChange={e => setRate(e.target.value)}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">
              {mode === 'loan' ? '貸款年限' : '期數（年）'}
            </label>
            <input
              type="number"
              min="1"
              value={years}
              onChange={e => setYears(e.target.value)}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
          試算結果
        </h2>
        {mode === 'compound' && compoundResult != null && (
          <p className="text-lg text-gray-900 dark:text-gray-100">
            {Y} 年後本利和：<strong>{compoundResult.toLocaleString()}</strong>{' '}
            元
          </p>
        )}
        {mode === 'loan' && monthly != null && (
          <div className="space-y-2 text-gray-900 dark:text-gray-100">
            <p className="text-lg">
              每月應還本息：<strong>{monthly.toLocaleString()}</strong> 元
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              總還款 {totalPayment?.toLocaleString()} 元，總利息{' '}
              {totalInterest?.toLocaleString()} 元
            </p>
          </div>
        )}
      </div>

      {(compoundYearlyData.length > 0 || loanYearlyData.length > 0) && (
        <section className="mb-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
            每年變化
          </h2>
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              {mode === 'compound' ? (
                <LineChart
                  data={compoundYearlyData}
                  margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    className="stroke-gray-200 dark:stroke-gray-600"
                  />
                  <XAxis
                    dataKey="year"
                    tick={{ fill: 'currentColor', fontSize: 12 }}
                    tickFormatter={v => `第 ${v} 年`}
                  />
                  <YAxis
                    tick={{ fill: 'currentColor', fontSize: 12 }}
                    tickFormatter={v => `${(v / 10000).toFixed(0)} 萬`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--tooltip-bg, #fff)',
                      border: '1px solid var(--tooltip-border, #e5e7eb)',
                      borderRadius: '8px',
                    }}
                    formatter={(value: unknown) => [
                      typeof value === 'number'
                        ? value.toLocaleString() + ' 元'
                        : String(value),
                      '本利和',
                    ]}
                    labelFormatter={label => `第 ${label} 年`}
                  />
                  <Line
                    type="monotone"
                    dataKey="本利和"
                    stroke="var(--color-primary, #2563eb)"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="本利和"
                  />
                </LineChart>
              ) : (
                <LineChart
                  data={loanYearlyData}
                  margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    className="stroke-gray-200 dark:stroke-gray-600"
                  />
                  <XAxis
                    dataKey="year"
                    tick={{ fill: 'currentColor', fontSize: 12 }}
                    tickFormatter={v => `第 ${v} 年`}
                  />
                  <YAxis
                    tick={{ fill: 'currentColor', fontSize: 12 }}
                    tickFormatter={v => `${(v / 10000).toFixed(0)} 萬`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--tooltip-bg, #fff)',
                      border: '1px solid var(--tooltip-border, #e5e7eb)',
                      borderRadius: '8px',
                    }}
                    formatter={(value: unknown, name: unknown) => [
                      typeof value === 'number'
                        ? value.toLocaleString() + ' 元'
                        : String(value),
                      String(name ?? ''),
                    ]}
                    labelFormatter={label => `第 ${label} 年`}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="剩餘本金"
                    stroke="var(--color-primary, #2563eb)"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="剩餘本金"
                  />
                  <Line
                    type="monotone"
                    dataKey="累計已還利息"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="累計已還利息"
                  />
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>
        </section>
      )}

      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
          使用說明
        </h2>
        <p className="leading-relaxed text-gray-600 dark:text-gray-400">
          複利試算：輸入本金、年利率與期數，計算到期本利和。房貸試算：輸入貸款金額、年利率與年限，計算每月本息攤還額與總利息。僅供參考，實際以銀行為準。
        </p>
      </section>
    </>
  );
}
